import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, update, remove } from "firebase/database";
import { app } from "./firebase/config";
import AdminPostManagement from "./AdminPostManagement";
import AdminAutoPostConfigs from "./AdminAutoPostConfigs";
import { POST_LENGTHS } from "./constants/postSettings";
import { getUsersPaginated } from "./utils/databaseUtils";
import AdminEmailUserCreation from "./components/AdminEmailUserCreation";
import AdminBulkEmailUserCreation from "./components/AdminBulkEmailUserCreation";
import {
  FaGoogle,
  FaEnvelope,
  FaPlus,
  FaUserShield,
  FaUserSlash,
  FaUsers,
} from "react-icons/fa";
const AdminUserManagement = ({ setActiveMenu }) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserAutoPostConfigs, setSelectedUserAutoPostConfigs] =
    useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [nextKey, setNextKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // New states for email user management
  const [showCreateEmailUser, setShowCreateEmailUser] = useState(false);
  const [showBulkCreateEmailUser, setShowBulkCreateEmailUser] = useState(false);
  const [authFilter, setAuthFilter] = useState("all"); // 'all', 'google', 'email'
  const [userStats, setUserStats] = useState(null);
  const [adminId] = useState("admin_" + Date.now()); // In real app, get from auth context

  const calculateUserStats = (userList) => {
    const total = userList.length;
    const google = userList.filter((u) => u.authProvider === "google").length;
    const email = userList.filter((u) => u.authProvider === "email").length;
    const emailActive = userList.filter(
      (u) => u.authProvider === "email" && u.isActive
    ).length;
    const emailInactive = userList.filter(
      (u) => u.authProvider === "email" && !u.isActive
    ).length;
    const premium = userList.filter((u) => {
      if (!u.membership || !u.membershipExpiry) return false;
      try {
        return new Date(u.membershipExpiry) > new Date();
      } catch {
        return false;
      }
    }).length;

    return {
      total,
      google,
      email,
      emailActive,
      emailInactive,
      premium,
      free: total - premium,
    };
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? "↑" : "↓";
    }
    return "";
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
      if (key === "membershipExpiry" || key === "createdAt") {
        const dateA = new Date(a[key] || 0);
        const dateB = new Date(b[key] || 0);
        return direction === "ascending" ? dateA - dateB : dateB - dateA;
      } else if (key === "totalPostCount" || key === "totalCreatedCount") {
        return direction === "ascending"
          ? (a[key] || 0) - (b[key] || 0)
          : (b[key] || 0) - (a[key] || 0);
      }
      return 0;
    });

    setFilteredUsers(sortedUsers);
  };

  // Phân trang
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Lấy dữ liệu người dùng từ Firebase
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        // Use new database utility with pagination
        const result = await getUsersPaginated(20);

        if (result.success) {
          setUsers(result.users);
          // filteredUsers will be updated by the other useEffect
          setNextKey(result.nextKey);
          setUserStats(calculateUserStats(result.users));
        } else {
          console.error("Error fetching users:", result.error);
          setUsers([]);
          setNextKey(null);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu người dùng:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleLoadMore = async () => {
    if (!nextKey || loadingMore) return;

    setLoadingMore(true);
    try {
      const result = await getUsersPaginated(20, nextKey);

      if (result.success) {
        setUsers((prev) => [...prev, ...result.users]);
        setNextKey(result.nextKey);
        // Stats will be updated when users state changes if we move stats calc to useEffect or here
        // But currently stats are calculated only on initial load in the original code?
        // No, line 119 set it. We should update it.
        setUserStats(calculateUserStats([...users, ...result.users]));
      }
    } catch (error) {
      console.error("Error loading more users:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Lọc người dùng theo từ khóa tìm kiếm và auth provider
  useEffect(() => {
    let filtered = [...users];

    // Filter by auth provider
    if (authFilter !== "all") {
      filtered = filtered.filter((user) => user.authProvider === authFilter);
    }

    // Filter by search text
    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((user) => {
        return (
          (user.id && user.id.toLowerCase().includes(searchLower)) ||
          (user.name && user.name.toLowerCase().includes(searchLower)) ||
          (user.profile?.displayName &&
            user.profile.displayName.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.profile?.email &&
            user.profile.email.toLowerCase().includes(searchLower)) ||
          (user.phone && user.phone.toLowerCase().includes(searchLower))
        );
      });
    }

    // Filter by date range with UTC+7
    if (fromDate || toDate) {
      filtered = filtered.filter((user) => {
        if (!user.createdAt) return false;

        try {
          const userDate = new Date(user.createdAt);

          // Kiểm tra tính hợp lệ của ngày
          if (isNaN(userDate.getTime())) {
            return false;
          }

          // Server đã được cấu hình múi giờ +7 nên không cần điều chỉnh thêm
          // userDate.setHours(userDate.getHours() + 7);

          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999); // Set to end of day
            return userDate >= from && userDate <= to;
          } else if (fromDate) {
            const from = new Date(fromDate);
            return userDate >= from;
          } else if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999); // Set to end of day
            return userDate <= to;
          }
          return true;
        } catch (error) {
          console.error("Lỗi khi lọc theo ngày:", error, user.createdAt);
          return false; // Loại bỏ record nếu có lỗi xử lý ngày
        }
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchText, users, fromDate, toDate, authFilter]);

  // Xử lý hiển thị chi tiết người dùng
  const handleUserClick = (user) => {
    setCurrentUser(user);
    setEditMode(false);
    setShowUserModal(true);
  };

  // Xử lý chỉnh sửa người dùng
  const handleEditUser = () => {
    setEditMode(true);
  };

  // Xử lý lưu thông tin người dùng
  const handleSaveUser = async () => {
    // Hiển thị hộp thoại xác nhận trước khi lưu
    if (!window.confirm("Bạn có chắc chắn muốn lưu những thay đổi này?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const userRef = ref(db, `users/${currentUser.id}`);

      // Loại bỏ trường id để tránh lưu trùng
      const { id, ...userData } = currentUser;

      // Đảm bảo email và displayName được lưu vào đúng path
      if (!userData.profile) userData.profile = {};

      // Xử lý email
      if (userData.profile.email) {
        // Email đã được cập nhật trong profile
      } else if (userData.email) {
        // Nếu không có email trong profile nhưng có trong userData, chuyển nó vào profile
        userData.profile.email = userData.email;
      }

      // Xử lý displayName
      if (userData.profile.displayName) {
        // displayName đã được cập nhật trong profile
      } else if (userData.name) {
        // Nếu không có displayName trong profile nhưng có trong userData, chuyển nó vào profile
        userData.profile.displayName = userData.name;
      }

      // Đảm bảo membership được lưu dưới dạng số nguyên
      if (
        userData.membership !== undefined &&
        userData.membership !== null &&
        userData.membership !== ""
      ) {
        userData.membership =
          typeof userData.membership === "string"
            ? parseInt(userData.membership)
            : userData.membership;
      }

      await update(userRef, userData);
      setEditMode(false);

      // Cập nhật state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, ...userData } : user
        )
      );

      // Hiển thị thông báo thành công
      alert("Cập nhật thông tin người dùng thành công!");
    } catch (error) {
      console.error("Lỗi khi cập nhật thông tin người dùng:", error);
      alert("Đã xảy ra lỗi khi cập nhật thông tin người dùng!");
    }
  };

  // Xử lý xóa người dùng
  const handleDeleteUser = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa người dùng này?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const userRef = ref(db, `users/${currentUser.id}`);

      await remove(userRef);
      setShowUserModal(false);

      // Cập nhật state
      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== currentUser.id)
      );
    } catch (error) {
      console.error("Lỗi khi xóa người dùng:", error);
      alert("Đã xảy ra lỗi khi xóa người dùng!");
    }
  };

  // Xử lý thay đổi giá trị input
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Xử lý đặc biệt cho trường membership, chuyển đổi từ chuỗi sang số
    if (name === "membership") {
      const membershipValue = value === "" ? "" : parseInt(value);

      // Tính ngày hết hạn mới dựa trên loại gói
      let newExpiryDate = null;
      if (membershipValue) {
        newExpiryDate = new Date();

        // Cộng thêm số ngày dựa trên loại membership
        switch (membershipValue) {
          case 1: // Gói 7 ngày
            newExpiryDate.setDate(newExpiryDate.getDate() + 7);
            break;
          case 2: // Gói 30 ngày
            newExpiryDate.setDate(newExpiryDate.getDate() + 30);
            break;
          case 3: // Gói 180 ngày
            newExpiryDate.setDate(newExpiryDate.getDate() + 180);
            break;
          case 4: // Gói 360 ngày
            newExpiryDate.setDate(newExpiryDate.getDate() + 360);
            break;
          default:
            // Mặc định 30 ngày nếu không xác định được
            newExpiryDate.setDate(newExpiryDate.getDate() + 30);
        }

        // Làm tròn đến giờ tiếp theo
        newExpiryDate.setMinutes(0);
        newExpiryDate.setSeconds(0);
        newExpiryDate.setMilliseconds(0);
        newExpiryDate.setHours(newExpiryDate.getHours() + 1);
      }

      setCurrentUser({
        ...currentUser,
        [name]: membershipValue,
        // Cập nhật membershipExpiry nếu đã chọn gói
        ...(newExpiryDate && { membershipExpiry: newExpiryDate.getTime() }),
      });
    } else {
      setCurrentUser({
        ...currentUser,
        [name]: value,
      });
    }
  };

  // Xử lý thay đổi trang
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Lấy label cho độ dài bài viết
  const getPostLengthLabel = (postLength) => {
    const foundLength = POST_LENGTHS.find(
      (length) => length.value === postLength
    );
    return foundLength ? foundLength.label : "Chưa thiết lập";
  };

  // Email User Management Handlers
  const handleCreateEmailUser = () => {
    setShowCreateEmailUser(true);
  };

  const handleBulkCreateEmailUser = () => {
    setShowBulkCreateEmailUser(true);
  };

  const handleEmailUserCreated = (newUser) => {
    // Refresh users list (reset to first page)
    const fetchUsers = async () => {
      const result = await getUsersPaginated(20);
      if (result.success) {
        setUsers(result.users);
        setNextKey(result.nextKey);
        setUserStats(calculateUserStats(result.users));
      }
    };

    fetchUsers();
    setShowCreateEmailUser(false);
  };

  const handleBulkEmailUsersCreated = (newUsers) => {
    // Refresh users list (reset to first page)
    const fetchUsers = async () => {
      const result = await getUsersPaginated(20);
      if (result.success) {
        setUsers(result.users);
        setNextKey(result.nextKey);
        setUserStats(calculateUserStats(result.users));
      }
    };

    fetchUsers();
    setShowBulkCreateEmailUser(false);
  };

  const handleCancelCreateEmailUser = () => {
    setShowCreateEmailUser(false);
  };

  const handleCancelBulkCreateEmailUser = () => {
    setShowBulkCreateEmailUser(false);
  };

  // Get auth provider icon
  const getAuthProviderIcon = (authProvider) => {
    switch (authProvider) {
      case "google":
        return <FaGoogle className="text-blue-500" title="Google User" />;
      case "email":
        return <FaEnvelope className="text-green-500" title="Email User" />;
      default:
        return <FaGoogle className="text-gray-400" title="Unknown" />;
    }
  };

  // Get user status icon for email users
  const getUserStatusIcon = (user) => {
    if (user.authProvider === "email") {
      return user.isActive ? (
        <FaUserShield className="text-green-500" title="Active" />
      ) : (
        <FaUserSlash className="text-red-500" title="Disabled" />
      );
    }
    return null;
  };

  // Handle toggle email user status
  const handleToggleEmailUserStatus = async (user) => {
    const action = user.isActive ? "vô hiệu hóa" : "kích hoạt";
    const confirmMessage = `Bạn có chắc chắn muốn ${action} tài khoản email của ${
      user.name || user.email
    }?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const { toggleEmailUserStatus } = await import(
        "./services/emailAuthService"
      );
      const result = await toggleEmailUserStatus(
        user.id,
        !user.isActive,
        adminId
      );

      if (result.success) {
        // Update local state
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === user.id ? { ...u, isActive: !u.isActive } : u
          )
        );

        alert(`Đã ${action} tài khoản thành công!`);
      } else {
        alert(`Lỗi khi ${action} tài khoản: ${result.error}`);
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      alert(`Đã xảy ra lỗi khi ${action} tài khoản!`);
    }
  };

  // Handle reset email user password
  const handleResetEmailUserPassword = async (user) => {
    const confirmMessage = `Bạn có chắc chắn muốn reset mật khẩu cho ${
      user.name || user.email
    }?\nMật khẩu mới sẽ được tạo tự động.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const { adminResetPassword } = await import(
        "./services/emailAuthService"
      );
      const result = await adminResetPassword(user.id, null, adminId);

      if (result.success) {
        const message = `Reset mật khẩu thành công!\n\nMật khẩu mới: ${result.newPassword}\n\nVui lòng sao chép và gửi cho người dùng.`;
        alert(message);

        // Copy to clipboard
        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(result.newPassword);
            alert("Mật khẩu mới đã được sao chép vào clipboard!");
          } catch (clipboardError) {
            console.error("Failed to copy to clipboard:", clipboardError);
          }
        }
      } else {
        alert(`Lỗi khi reset mật khẩu: ${result.error}`);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Đã xảy ra lỗi khi reset mật khẩu!");
    }
  };

  // Handler for viewing auto post configs
  const handleViewAutoPostConfigs = (user) => {
    setSelectedUserAutoPostConfigs({
      userId: user.id,
      userName: user.profile?.displayName || user.name || user.id,
    });
  };

  // Tạo phân trang
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === i
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex flex-wrap justify-center mt-4 gap-1">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          &laquo;
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-2 md:px-3 py-1 rounded ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          &raquo;
        </button>
      </div>
    );
  };

  // Render Load More Button
  const renderLoadMore = () => {
    if (!nextKey) return null;

    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={handleLoadMore}
          disabled={loadingMore}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          {loadingMore ? "Đang tải..." : "Tải thêm người dùng"}
        </button>
      </div>
    );
  };

  // Hiển thị trạng thái membership
  const renderMembershipStatus = (user) => {
    // Nếu không có membership hoặc membership = 0
    if (!user.membership) {
      return (
        <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">
          Free
        </span>
      );
    }

    // Kiểm tra hợp lệ trước khi so sánh Date
    const isExpired = (() => {
      if (!user.membershipExpiry) return true;

      try {
        const expiryDate = new Date(user.membershipExpiry);
        const now = new Date();

        // Kiểm tra xem Date có hợp lệ không
        if (isNaN(expiryDate.getTime())) {
          return true; // Coi như đã hết hạn nếu không thể xác định
        }

        return expiryDate < now;
      } catch (error) {
        console.error("Lỗi khi kiểm tra hạn membership:", error);
        return true; // Coi như đã hết hạn nếu có lỗi
      }
    })();

    if (isExpired) {
      return (
        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
          Hết hạn
        </span>
      );
    } else {
      // Hiển thị loại plan dựa trên membership ID
      let planName = "Premium";

      // Dựa vào ID của plan trong payosService.js
      // Chuyển sang so sánh chính xác bằng ===
      const membershipId =
        typeof user.membership === "string"
          ? parseInt(user.membership)
          : user.membership;

      switch (membershipId) {
        case 1:
          planName = "7 ngày";
          break;
        case 2:
          planName = "30 ngày";
          break;
        case 3:
          planName = "180 ngày";
          break;
        case 4:
          planName = "360 ngày";
          break;
        default:
          planName = "Premium";
      }

      return (
        <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
          {planName}
        </span>
      );
    }
  };

  // Handle navigation back from auto post configs
  const handleBackFromAutoPostConfigs = () => {
    setSelectedUserAutoPostConfigs(null);
  };

  // Handle navigation back from post management
  const handleBackFromPostManagement = () => {
    setSelectedUserId(null);
  };

  if (selectedUserAutoPostConfigs) {
    return (
      <AdminAutoPostConfigs
        userId={selectedUserAutoPostConfigs.userId}
        userName={selectedUserAutoPostConfigs.userName}
        onBack={handleBackFromAutoPostConfigs}
      />
    );
  }

  if (selectedUserId) {
    return (
      <>
        {setActiveMenu &&
          typeof setActiveMenu === "function" &&
          setActiveMenu("posts")}
        <AdminPostManagement
          userId={selectedUserId}
          onBack={handleBackFromPostManagement}
        />
      </>
    );
  }

  return (
    <div className="p-6 admin-user-management">
      <div className="mb-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-6">
          {/* Tổng số user */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Tổng số user
            </h3>
            <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
              {filteredUsers.length}
            </p>
            <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
              {fromDate || toDate
                ? "Trong khoảng thời gian đã chọn"
                : "Tất cả user trong hệ thống"}
            </p>
          </div>

          {/* User Premium/Free */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Premium/Free
            </h3>
            {(() => {
              const premiumUsers = filteredUsers.filter((u) => {
                // Nếu không có membership, user là Free
                if (!u.membership) return false;

                // Kiểm tra ngày hết hạn
                if (!u.membershipExpiry) return false;

                try {
                  const expiryDate = new Date(u.membershipExpiry);
                  const now = new Date();

                  // Kiểm tra xem Date có hợp lệ không
                  if (isNaN(expiryDate.getTime())) {
                    return false; // Coi như Free nếu ngày không hợp lệ
                  }

                  return expiryDate > now;
                } catch (error) {
                  console.error("Lỗi khi kiểm tra premium user:", error);
                  return false; // Coi như Free nếu có lỗi
                }
              });

              const freeUsers = filteredUsers.filter((u) => {
                // Nếu không có membership, user là Free
                if (!u.membership) return true;

                // Kiểm tra ngày hết hạn
                if (!u.membershipExpiry) return true;

                try {
                  const expiryDate = new Date(u.membershipExpiry);
                  const now = new Date();

                  // Kiểm tra xem Date có hợp lệ không
                  if (isNaN(expiryDate.getTime())) {
                    return true; // Coi như Free nếu ngày không hợp lệ
                  }

                  return expiryDate <= now;
                } catch (error) {
                  console.error("Lỗi khi kiểm tra free user:", error);
                  return true; // Coi như Free nếu có lỗi
                }
              });

              const premiumPercentage = filteredUsers.length
                ? ((premiumUsers.length / filteredUsers.length) * 100).toFixed(
                    1
                  )
                : 0;
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">{`${premiumUsers.length}/${freeUsers.length}`}</p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
                    {`${premiumPercentage}% người dùng Premium`}
                  </p>
                </>
              );
            })()}
          </div>

          {/* Tổng số bài viết đã tạo */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              Bài viết đã tạo
            </h3>
            {(() => {
              const totalCreatedPosts = filteredUsers.reduce(
                (sum, user) => sum + (user.totalCreatedCount || 0),
                0
              );
              const totalPostedPosts = filteredUsers.reduce(
                (sum, user) => sum + (user.totalPostCount || 0),
                0
              );
              const todayCreatedPosts = filteredUsers.reduce(
                (sum, user) => sum + (user.todayCreatedPosts || 0),
                0
              );
              const avgCreatedPosts = filteredUsers.length
                ? (totalCreatedPosts / filteredUsers.length).toFixed(1)
                : 0;
              const activeCreators = filteredUsers.filter(
                (user) => (user.totalCreatedCount || 0) > 0
              ).length;
              const activePercentage = filteredUsers.length
                ? ((activeCreators / filteredUsers.length) * 100).toFixed(1)
                : 0;
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-blue-600 mt-1 md:mt-2">
                    {totalCreatedPosts}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-3">
                    <span className="text-green-600">
                      Đăng: {totalPostedPosts}
                    </span>
                    {todayCreatedPosts > 0 && (
                      <>
                        <br />
                        <span className="text-orange-600">
                          Hôm nay: +{todayCreatedPosts}
                        </span>
                      </>
                    )}
                    <br />
                    {`${activePercentage}% user có tạo bài`}
                  </p>
                </>
              );
            })()}
          </div>

          {/* User mới */}
          <div className="bg-white rounded-lg shadow p-2 md:p-4 hover:shadow-lg transition-all duration-300">
            <h3 className="text-xs md:text-sm font-medium text-gray-500">
              {fromDate || toDate
                ? "User trong khoảng thời gian"
                : "User mới (7 ngày)"}
            </h3>
            {(() => {
              const newUsers =
                fromDate || toDate
                  ? filteredUsers.length
                  : filteredUsers.filter((user) => {
                      const userDate = new Date(user.createdAt);
                      const sevenDaysAgo = new Date();
                      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                      return userDate >= sevenDaysAgo;
                    }).length;
              const percentage = filteredUsers.length
                ? ((newUsers / filteredUsers.length) * 100).toFixed(1)
                : 0;
              const avgPerDay =
                fromDate && toDate
                  ? (
                      newUsers /
                      ((new Date(toDate) - new Date(fromDate)) /
                        (1000 * 60 * 60 * 24))
                    ).toFixed(1)
                  : (newUsers / 7).toFixed(1);
              return (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 md:mt-2">
                    {newUsers}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 line-clamp-2">
                    {fromDate || toDate
                      ? `${percentage}% của tổng số user`
                      : `Trung bình ${avgPerDay} user/ngày`}
                  </p>
                </>
              );
            })()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Quản lý người dùng
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleCreateEmailUser}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm"
                >
                  <FaPlus />
                  <span>Tạo Email User</span>
                </button>
                <button
                  onClick={handleBulkCreateEmailUser}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm"
                >
                  <FaUsers />
                  <span>Tạo Đồng Loạt</span>
                </button>
              </div>
            </div>

            {/* Auth Filter */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lọc theo phương thức đăng nhập
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setAuthFilter("all")}
                  className={`px-3 py-1 rounded-md text-sm ${
                    authFilter === "all"
                      ? "bg-blue-100 text-blue-800 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Tất cả ({userStats?.total || 0})
                </button>
                <button
                  onClick={() => setAuthFilter("google")}
                  className={`px-3 py-1 rounded-md text-sm flex items-center space-x-1 ${
                    authFilter === "google"
                      ? "bg-blue-100 text-blue-800 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <FaGoogle />
                  <span>Google ({userStats?.google || 0})</span>
                </button>
                <button
                  onClick={() => setAuthFilter("email")}
                  className={`px-3 py-1 rounded-md text-sm flex items-center space-x-1 ${
                    authFilter === "email"
                      ? "bg-blue-100 text-blue-800 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <FaEnvelope />
                  <span>Email ({userStats?.email || 0})</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Search input */}
              <div className="col-span-1 md:col-span-4">
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Tìm kiếm
                </label>
                <div className="relative">
                  <input
                    id="search"
                    type="text"
                    placeholder="Tìm kiếm theo ID, tên, email..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full h-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchText && (
                    <button
                      onClick={() => setSearchText("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Date range filter */}
              <div className="col-span-1 md:col-span-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lọc theo ngày tạo
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <input
                      id="fromDate"
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full h-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Từ ngày"
                    />
                    {fromDate && (
                      <button
                        onClick={() => setFromDate("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="flex-1 relative">
                    <input
                      id="toDate"
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full h-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Đến ngày"
                    />
                    {toDate && (
                      <button
                        onClick={() => setToDate("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {(fromDate || toDate) && (
                    <button
                      onClick={() => {
                        setFromDate("");
                        setToDate("");
                      }}
                      className="h-10 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm flex items-center"
                    >
                      Xóa bộ lọc
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-600">
                  Đang tải dữ liệu người dùng...
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto -mx-6 md:mx-0">
                  <table className="min-w-full bg-white table-auto md:table-fixed">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 uppercase text-xs md:text-sm leading-normal">
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/6">
                          ID
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/6">
                          Tên
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/12">
                          Auth
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/12">
                          Membership
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/6"
                          onClick={() => handleSort("membershipExpiry")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("membershipExpiry")}
                          >
                            <span className="hidden md:inline">
                              Ngày hết hạn
                            </span>
                            <span className="md:hidden">Hết hạn</span>
                            {getSortIndicator("membershipExpiry")}
                          </div>
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-left w-1/6">
                          <div className="flex flex-col">
                            <span className="font-medium">Bài viết</span>
                            <div className="flex gap-2 mt-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSort("totalCreatedCount");
                                }}
                                className={`text-xs px-1 py-0.5 rounded ${
                                  sortConfig.key === "totalCreatedCount"
                                    ? "bg-blue-100 text-blue-700"
                                    : "text-gray-500 hover:text-gray-700"
                                }`}
                              >
                                Tạo {getSortIndicator("totalCreatedCount")}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSort("totalPostCount");
                                }}
                                className={`text-xs px-1 py-0.5 rounded ${
                                  sortConfig.key === "totalPostCount"
                                    ? "bg-green-100 text-green-700"
                                    : "text-gray-500 hover:text-gray-700"
                                }`}
                              >
                                Đăng {getSortIndicator("totalPostCount")}
                              </button>
                            </div>
                          </div>
                        </th>
                        <th
                          className="py-2 px-2 md:py-3 md:px-6 text-left cursor-pointer w-1/6"
                          onClick={() => handleSort("createdAt")}
                        >
                          <div
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort("createdAt")}
                          >
                            <span className="hidden md:inline">Ngày tạo</span>
                            <span className="md:hidden">Ngày</span>
                            {getSortIndicator("createdAt")}
                          </div>
                        </th>
                        <th className="py-2 px-2 md:py-3 md:px-6 text-center w-1/12">
                          Hành động
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm">
                      {currentUsers.length > 0 ? (
                        currentUsers.map((user) => (
                          <tr
                            key={user.id}
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap max-w-[80px] md:max-w-[100px] truncate text-xs md:text-sm">
                              {user.id}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left max-w-[80px] md:max-w-[150px] truncate text-xs md:text-sm">
                              {user.profile?.displayName ||
                                user.name ||
                                "Chưa có tên"}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              <div className="flex items-center space-x-1">
                                {getAuthProviderIcon(user.authProvider)}
                                {getUserStatusIcon(user)}
                              </div>
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              {renderMembershipStatus(user)}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              {(() => {
                                if (!user.membershipExpiry) return "Chưa có";

                                try {
                                  const expiryDate = new Date(
                                    user.membershipExpiry
                                  );

                                  // Kiểm tra xem Date có hợp lệ không
                                  if (isNaN(expiryDate.getTime())) {
                                    return "Không xác định";
                                  }

                                  return expiryDate.toLocaleDateString("vi-VN");
                                } catch (error) {
                                  console.error(
                                    "Lỗi xử lý membershipExpiry:",
                                    error
                                  );
                                  return "Không xác định";
                                }
                              })()}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left text-xs md:text-sm">
                              <div className="grid grid-cols-2 gap-2">
                                {/* Cột trái: Đã tạo */}
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-500 mb-1">
                                    Đã tạo
                                  </span>
                                  <span className="font-bold text-blue-600">
                                    {user.totalCreatedCount || 0}
                                  </span>
                                  {user.todayCreatedPosts > 0 && (
                                    <span className="text-xs text-green-600">
                                      +{user.todayCreatedPosts}
                                    </span>
                                  )}
                                </div>

                                {/* Cột phải: Đã đăng */}
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-500 mb-1">
                                    Đã đăng
                                  </span>
                                  <span className="font-bold text-green-600">
                                    {user.totalPostCount || 0}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {user.postCounts || "0/0"}
                                  </span>
                                </div>
                              </div>

                              {/* Tỷ lệ đăng bài (chỉ hiển thị nếu có data) */}
                              {(user.totalCreatedCount || 0) > 0 && (
                                <div className="mt-1 pt-1 border-t border-gray-100">
                                  <span className="text-xs text-gray-500">
                                    Tỷ lệ đăng:{" "}
                                    <span className="font-medium">
                                      {(
                                        ((user.totalPostCount || 0) /
                                          (user.totalCreatedCount || 1)) *
                                        100
                                      ).toFixed(0)}
                                      %
                                    </span>
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-left whitespace-nowrap text-xs md:text-sm">
                              {(() => {
                                if (!user.createdAt) return "";

                                try {
                                  const createdDate = new Date(user.createdAt);

                                  // Kiểm tra xem Date có hợp lệ không
                                  if (isNaN(createdDate.getTime())) {
                                    return "Không xác định";
                                  }

                                  return createdDate.toLocaleString("vi-VN");
                                } catch (error) {
                                  console.error("Lỗi xử lý createdAt:", error);
                                  return "Không xác định";
                                }
                              })()}
                            </td>
                            <td className="py-2 px-2 md:py-3 md:px-6 text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <button
                                  onClick={() => handleUserClick(user)}
                                  className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded-md text-xs transition duration-300"
                                  title="Chi tiết"
                                >
                                  Chi tiết
                                </button>
                                {user.authProvider === "email" && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleToggleEmailUserStatus(user)
                                      }
                                      className={`py-1 px-2 rounded-md text-xs transition duration-300 ${
                                        user.isActive
                                          ? "bg-red-500 hover:bg-red-600 text-white"
                                          : "bg-green-500 hover:bg-green-600 text-white"
                                      }`}
                                      title={
                                        user.isActive
                                          ? "Vô hiệu hóa"
                                          : "Kích hoạt"
                                      }
                                    >
                                      {user.isActive ? "Vô hiệu" : "Kích hoạt"}
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleResetEmailUserPassword(user)
                                      }
                                      className="bg-orange-500 hover:bg-orange-600 text-white py-1 px-2 rounded-md text-xs transition duration-300"
                                      title="Reset mật khẩu"
                                    >
                                      Reset PW
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="9" // Updated colspan after merging post columns
                            className="py-6 text-center text-gray-500"
                          >
                            Không tìm thấy người dùng nào
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
                {renderLoadMore()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Chi tiết người dùng */}
      {showUserModal && currentUser && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4">
          <div className="relative bg-white rounded-lg shadow-lg max-w-4xl w-full mx-auto max-h-[90vh] overflow-auto">
            <div className="px-3 md:px-6 py-3 md:py-4 border-b flex justify-between items-center">
              <h3 className="text-base md:text-lg font-medium text-gray-900">
                {editMode
                  ? "Chỉnh sửa thông tin người dùng"
                  : "Chi tiết người dùng"}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-3 md:p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    ID
                  </label>
                  <input
                    type="text"
                    value={currentUser.id || ""}
                    disabled
                    className="w-full px-2 md:px-3 py-1 md:py-2 border rounded-md bg-gray-50 text-gray-500 break-all text-xs md:text-sm"
                  />
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Tên
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={
                      currentUser.profile?.displayName || currentUser.name || ""
                    }
                    onChange={(e) => {
                      // Cập nhật displayName trong profile
                      const updatedUser = { ...currentUser };
                      if (!updatedUser.profile) updatedUser.profile = {};
                      updatedUser.profile.displayName = e.target.value;
                      setCurrentUser(updatedUser);
                    }}
                    disabled={!editMode}
                    className={`w-full px-2 md:px-3 py-1 md:py-2 border rounded-md text-xs md:text-sm ${
                      !editMode
                        ? "bg-gray-50 text-gray-500"
                        : "focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  />
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={
                      currentUser.profile?.email || currentUser.email || ""
                    }
                    onChange={(e) => {
                      // Cập nhật email trong profile
                      const updatedUser = { ...currentUser };
                      if (!updatedUser.profile) updatedUser.profile = {};
                      updatedUser.profile.email = e.target.value;
                      setCurrentUser(updatedUser);
                    }}
                    disabled={!editMode}
                    className={`w-full px-2 md:px-3 py-1 md:py-2 border rounded-md text-xs md:text-sm ${
                      !editMode
                        ? "bg-gray-50 text-gray-500"
                        : "focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  />
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Điện thoại
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={currentUser.phone || ""}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    className={`w-full px-2 md:px-3 py-1 md:py-2 border rounded-md text-xs md:text-sm ${
                      !editMode
                        ? "bg-gray-50 text-gray-500"
                        : "focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  />
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Loại membership
                  </label>
                  {editMode ? (
                    <select
                      name="membership"
                      value={
                        currentUser.membership !== undefined &&
                        currentUser.membership !== null
                          ? currentUser.membership.toString()
                          : ""
                      }
                      onChange={handleInputChange}
                      className="w-full px-2 md:px-3 py-1 md:py-2 border rounded-md text-xs md:text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Free</option>
                      <option value="1">Gói 7 ngày</option>
                      <option value="2">Gói 30 ngày</option>
                      <option value="3">Gói 180 ngày</option>
                      <option value="4">Gói 360 ngày</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={(() => {
                        if (!currentUser.membership) return "Free";
                        // Chuyển đổi membership thành số nguyên để so sánh
                        const membershipId =
                          typeof currentUser.membership === "string"
                            ? parseInt(currentUser.membership)
                            : currentUser.membership;

                        switch (membershipId) {
                          case 1:
                            return "Gói 7 ngày";
                          case 2:
                            return "Gói 30 ngày";
                          case 3:
                            return "Gói 180 ngày";
                          case 4:
                            return "Gói 360 ngày";
                          default:
                            return `Premium (${currentUser.membership})`;
                        }
                      })()}
                      disabled
                      className="w-full px-2 md:px-3 py-1 md:py-2 border rounded-md bg-gray-50 text-gray-500 text-xs md:text-sm"
                    />
                  )}
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Ngày hết hạn membership
                  </label>
                  <input
                    type="datetime-local"
                    name="membershipExpiry"
                    value={(() => {
                      if (!currentUser.membershipExpiry) return "";

                      try {
                        const expiryDate = new Date(
                          currentUser.membershipExpiry
                        );

                        // Kiểm tra xem Date có hợp lệ không
                        if (isNaN(expiryDate.getTime())) {
                          return "";
                        }

                        return expiryDate.toISOString().slice(0, 16);
                      } catch (error) {
                        console.error("Lỗi xử lý membershipExpiry:", error);
                        return "";
                      }
                    })()}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    className={`w-full px-2 md:px-3 py-1 md:py-2 border rounded-md text-xs md:text-sm ${
                      !editMode
                        ? "bg-gray-50 text-gray-500"
                        : "focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  />
                </div>
                <div className="mb-3 md:mb-4">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Ngày tạo
                  </label>
                  <input
                    type="text"
                    value={(() => {
                      if (!currentUser.createdAt) return "";

                      try {
                        const createdDate = new Date(currentUser.createdAt);

                        // Kiểm tra xem Date có hợp lệ không
                        if (isNaN(createdDate.getTime())) {
                          return "Không xác định";
                        }

                        return createdDate.toLocaleString("vi-VN");
                      } catch (error) {
                        console.error("Lỗi xử lý createdAt:", error);
                        return "Không xác định";
                      }
                    })()}
                    disabled
                    className="w-full px-2 md:px-3 py-1 md:py-2 border rounded-md bg-gray-50 text-gray-500 text-xs md:text-sm"
                  />
                </div>

                {/* Post Creation Statistics */}
                <div className="mb-3 md:mb-4 col-span-1 md:col-span-2">
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Thống kê tạo bài viết
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-gray-50 rounded-md">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {currentUser.totalCreatedNormalPosts || 0}
                      </div>
                      <div className="text-xs text-gray-500">Đã tạo</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">
                        {(() => {
                          const posts = currentUser.posts || {};
                          const countNormal =
                            posts.normalPostGenerator?.links?.length || 0;
                          return countNormal;
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">Đã đăng</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-orange-600">
                        {currentUser.todayCreatedPosts || 0}
                      </div>
                      <div className="text-xs text-gray-500">Hôm nay</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-600">
                        {(() => {
                          const created =
                            currentUser.totalCreatedNormalPosts || 0;
                          const posted = (() => {
                            const posts = currentUser.posts || {};
                            return (
                              posts.normalPostGenerator?.links?.length || 0
                            );
                          })();
                          return created > 0
                            ? ((posted / created) * 100).toFixed(1) + "%"
                            : "0%";
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">Tỷ lệ đăng</div>
                    </div>
                  </div>
                  {currentUser.lastCreatedAt && (
                    <div className="mt-2 text-xs text-gray-500">
                      Tạo bài cuối:{" "}
                      {new Date(currentUser.lastCreatedAt).toLocaleString(
                        "vi-VN"
                      )}
                    </div>
                  )}
                </div>
              </div>

              {currentUser.fanpages && (
                <div className="mt-6">
                  <h5 className="text-sm md:text-md font-medium text-gray-900 mb-2 md:mb-3">
                    Fanpages đã kết nối
                  </h5>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 text-[10px] md:text-xs leading-normal">
                          <th className="py-1 md:py-2 px-2 md:px-4 text-left">
                            ID Fanpage
                          </th>
                          <th className="py-1 md:py-2 px-2 md:px-4 text-left">
                            Tên Fanpage
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-600 text-[10px] md:text-xs">
                        {Object.entries(currentUser.fanpages).map(
                          ([id, data]) => (
                            <tr key={id} className="border-b">
                              <td className="py-1 md:py-2 px-2 md:px-4 max-w-[100px] md:max-w-[150px] truncate">
                                {id}
                              </td>
                              <td className="py-1 md:py-2 px-2 md:px-4 max-w-[150px] md:max-w-[250px] truncate">
                                {data.name}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="px-3 md:px-6 py-3 md:py-4 border-t bg-gray-50 flex flex-wrap justify-end gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleSaveUser}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Lưu thay đổi
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleDeleteUser}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Xóa người dùng
                  </button>
                  <button
                    onClick={handleEditUser}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Chỉnh sửa
                  </button>
                  <button
                    onClick={() => setSelectedUserId(currentUser.id)}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Xem danh sách bài viết
                  </button>
                  <button
                    onClick={() => handleViewAutoPostConfigs(currentUser)}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Xem thiết lập đăng bài tự động
                  </button>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="px-3 md:px-4 py-1 md:py-2 text-xs md:text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Đóng
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email User Creation Modal */}
      {showCreateEmailUser && (
        <AdminEmailUserCreation
          onUserCreated={handleEmailUserCreated}
          onCancel={handleCancelCreateEmailUser}
          adminId={adminId}
        />
      )}

      {/* Bulk Email User Creation Modal */}
      {showBulkCreateEmailUser && (
        <AdminBulkEmailUserCreation
          onUsersCreated={handleBulkEmailUsersCreated}
          onCancel={handleCancelBulkCreateEmailUser}
          adminId={adminId}
        />
      )}
    </div>
  );
};

export default AdminUserManagement;
